import { Dispatch, SetStateAction, useState } from 'react'
import {
  CheckCircleOutline,
  DeleteOutline,
  OpenInFull,
} from '@mui/icons-material'
import { Box, Card, IconButton, Stack } from '@mui/material'
import { DocumentPage } from '@/types/documents'
import { DEFAULT_TILE_WIDTH } from './SplitDocumentPages'

type ImageCardProps = {
  page: DocumentPage
  pagesToDelete: number[]
  onShowFullScreenImage: () => void
  setPagesToDelete: Dispatch<SetStateAction<number[]>>
  tileWidth?: number
  useFullImage?: boolean
}

export default function ImageCard({
  page,
  pagesToDelete,
  onShowFullScreenImage,
  setPagesToDelete,
  tileWidth = DEFAULT_TILE_WIDTH,
  useFullImage,
}: ImageCardProps) {
  const [hovered, setHovered] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const pageIsToBeDeleted = pagesToDelete.includes(page.page_number)
  const showIcons = hovered || pageIsToBeDeleted

  return (
    <Card
      sx={{
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1.5,
        transition: 'box-shadow 0.15s ease-in-out',
        boxShadow: hovered ? 6 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
          width: tileWidth,
          height: tileWidth * 1.4,
          textAlign: 'center',
        }}
      >
        <img
          loading="lazy"
          style={{
            objectFit: 'contain',
            height: '100%',
            width: '100%',
            opacity: loaded ? 1 : 0,
            filter: pageIsToBeDeleted
              ? 'brightness(50%) grayscale(30%)'
              : undefined,
            transition: 'opacity 0.2s ease-in-out, filter 0.15s ease-in-out',
          }}
          src={useFullImage ? page.image_urls.full : page.image_urls.thumbnail}
          onLoad={() => setLoaded(true)}
          onError={(event) => {
            if (useFullImage) return
            const target = event.target as HTMLImageElement
            target.src = page.image_urls.full
            target.onerror = null
          }}
        />
      </Box>
      {/* Action overlay - visible on hover or when page is marked for deletion */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          opacity: showIcons ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          backgroundColor: 'rgba(0,0,0,0.5)',
          borderRadius: 1,
          p: 0.25,
        }}
      >
        <IconButton
          size="small"
          sx={{ color: 'white' }}
          onClick={() => {
            onShowFullScreenImage()
            setHovered(false)
          }}
        >
          <OpenInFull fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{
            color: pageIsToBeDeleted ? 'error.light' : 'white',
          }}
          onClick={() => {
            setPagesToDelete((prev) => {
              return pageIsToBeDeleted
                ? prev.filter((p) => p !== page.page_number)
                : [...prev, page.page_number]
            })
          }}
        >
          {pageIsToBeDeleted ? (
            <CheckCircleOutline fontSize="small" />
          ) : (
            <DeleteOutline fontSize="small" />
          )}
        </IconButton>
      </Stack>
      {/* Red tint overlay for pages marked for deletion */}
      {pageIsToBeDeleted && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(211, 47, 47, 0.12)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Card>
  )
}
